import {useQuery} from '@tanstack/react-query'
import {useFormLayout} from '@/hooks/useFormLayout'
import {AttributeValueInput} from '@/components/attributes/AttributeValueInput'
import {MultiValueAttributeInput} from '@/components/attributes/MultiValueAttributeInput'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {validateValue} from '@/lib/attribute-validation'
import {Skeleton} from '@/components/ui/skeleton'
import {useState} from 'react'
import {resolveTemplateExpression} from './resolveTemplateExpression'
import type {AttributeDefinition} from '@/types/attribute-definition'
import type {AttributeValue} from '@/types/attribute'
import {AttributeOperation} from '@/types/attribute-operation'
import type {AttributeLayoutField, LayoutSection} from '@/types/form-layout'
import {COL_SPAN_CLASS} from '@/types/form-layout'
import {LayoutActiveIndicator} from './LayoutActiveIndicator'
import type {SetAttributeFormValue} from '@/features/onboarding/types'
import {AttributeEntityType} from '@/types/attribute-entity-type'

/**
 * Renders attribute definitions in sections according to a saved form layout.
 *
 * SECURITY: This component MUST NOT use dangerouslySetInnerHTML for any value sourced from the
 * layout schema.
 *
 * FALLBACK: If no layout exists for useCaseKey (404), renders using the existing flat attribute
 * list sorted by sortOrder — zero regression in onboarding wizard behavior.
 */
interface AttributeLayoutRendererProps {
    useCaseKey: string
    entityType: 'PARTY' | 'IDENTITY'
    typeCode: string
    values: SetAttributeFormValue[]
    onChange: (values: SetAttributeFormValue[]) => void
    existingValues?: AttributeValue[]
    templateContext?: Record<string, string>
}

// ============================================================================
// Hook: fetch attribute definitions
// ============================================================================


const EntityTypePath: Record<'PARTY' | 'IDENTITY', AttributeEntityType> = {
    PARTY: AttributeEntityType.PARTY,
    IDENTITY: AttributeEntityType.IDENTITY,
}

function useDefinitions(entityType: 'PARTY' | 'IDENTITY', typeCode: string) {
    const services = useServices()
    const apiPath = EntityTypePath[entityType]
    const {data, isLoading} = useQuery({
        queryKey: ['attribute-definitions', entityType, typeCode],
        queryFn: () => services.attributeDefinitions.list(apiPath, {typeCode}),
        enabled: !!typeCode,
    })
    const definitions = (data ?? []).filter(
        (def) => def.active && def.operations.includes(AttributeOperation.CREATE)
    )
    return {definitions, isLoading}
}

// ============================================================================
// Single attribute field renderer
// ============================================================================

interface AttributeFieldProps {
    definition: AttributeDefinition
    values: SetAttributeFormValue[]
    onChange: (values: SetAttributeFormValue[]) => void
    existingValues?: AttributeValue[]
    labelOverride?: string
    defaultValueOverride?: string
    templateExpression?: string
    templateContext?: Record<string, string>
    disabled?: boolean
}

function AttributeField({
    definition,
    values,
    onChange,
    existingValues,
    labelOverride,
    defaultValueOverride,
    templateExpression,
    templateContext,
    disabled,
}: AttributeFieldProps) {
    const i18n = useI18n()
    const [touched, setTouched] = useState<Record<number, boolean>>({})
    const maxOccurrences = definition.validationRules?.maxOccurrences ?? 1
    const isMultiValued = maxOccurrences > 1

    // Resolve template expression for default value
    const resolvedDefault =
        templateExpression && templateContext
            ? resolveTemplateExpression(templateExpression, templateContext)
            : defaultValueOverride ?? undefined

    const existingValue = existingValues?.find((ev) => ev.definitionId === definition.id)
    const currentValues = values.filter((v) => v.attributeName === definition.name)

    const effectiveValues =
        currentValues.length > 0
            ? currentValues
            : existingValue
              ? [
                    {
                        attributeName: definition.name,
                        valueType: definition.valueType,
                        valueString:
                            typeof existingValue.value === 'string' ? existingValue.value : null,
                    } as SetAttributeFormValue,
                ]
              : []

    const displayValues =
        effectiveValues.length > 0
            ? effectiveValues
            : [
                  {
                      attributeName: definition.name,
                      valueType: definition.valueType,
                      valueString: resolvedDefault ?? '',
                  } as SetAttributeFormValue,
              ]

    const updateValue = (fieldValue: string) => {
        const otherValues = values.filter((v) => v.attributeName !== definition.name)
        const currentForDef = [...displayValues]
        currentForDef[0] = {
            ...currentForDef[0],
            valueString: fieldValue,
            attributeName: definition.name,
            valueType: definition.valueType,
        }
        onChange([...otherValues, ...currentForDef])
    }

    // Override the definition's display name if layout provides a label override
    const definitionWithOverride: AttributeDefinition = labelOverride
        ? {...definition, displayName: labelOverride}
        : definition

    if (isMultiValued) {
        return (
            <MultiValueAttributeInput
                definition={definitionWithOverride}
                values={values}
                onChange={onChange}
                disabled={disabled}
                testId={`attribute-${definition.name}`}
            />
        )
    }

    const singleVal = displayValues[0]?.valueString ?? ''
    const singleError = touched[0] ? validateValue(definition, singleVal, i18n) : null
    return (
        <div className="mt-1.5">
            <AttributeValueInput
                definition={definitionWithOverride}
                value={singleVal}
                onChange={updateValue}
                onBlur={() => setTouched((prev) => ({...prev, 0: true}))}
                error={singleError}
                testId={`attribute-${definition.name}`}
                showLabel
                disabled={disabled}
            />
        </div>
    )
}

// ============================================================================
// Section with layout-ordered fields
// ============================================================================

interface LayoutSectionProps {
    section: LayoutSection
    definitions: AttributeDefinition[]
    values: SetAttributeFormValue[]
    onChange: (values: SetAttributeFormValue[]) => void
    existingValues?: AttributeValue[]
    templateContext?: Record<string, string>
}

function LayoutAttributeSection({
    section,
    definitions,
    values,
    onChange,
    existingValues,
    templateContext,
}: LayoutSectionProps) {
    const defById = new Map(definitions.map((d) => [d.id, d]))

    const attrFields = section.fields.filter(
        (f): f is AttributeLayoutField => f.source === 'attribute'
    )

    const visibleFields = attrFields.filter((f) => {
        if (f.hidden) return false
        return defById.has(f.definitionId)
    })

    if (visibleFields.length === 0) return null

    return (
        <div className="space-y-2">
            {section.label && (
                <h3 className="text-sm font-semibold text-foreground" data-testid={`attr-section-title-${section.key}`}>{section.label}</h3>
            )}
            <div className="grid grid-cols-12 gap-3">
                {visibleFields.map((field, idx) => {
                    const def = defById.get(field.definitionId)
                    if (!def) return null

                    const defaultSpan = Math.floor(12 / (section.columns ?? 1))
                    const colSpan = Math.min(Math.max(field.colSpan ?? defaultSpan, 1), 12)

                    return (
                        <div key={idx} className={COL_SPAN_CLASS[colSpan]}>
                            <AttributeField
                                definition={def}
                                values={values}
                                onChange={onChange}
                                existingValues={existingValues}
                                labelOverride={field.label}

                                defaultValueOverride={field.defaultValue}
                                templateExpression={field.templateExpression}
                                templateContext={templateContext}
                                disabled={field.disabled}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ============================================================================
// AttributeLayoutRenderer (main export)
// ============================================================================

export function AttributeLayoutRenderer({
    useCaseKey,
    entityType,
    typeCode,
    values,
    onChange,
    existingValues,
    templateContext,
}: AttributeLayoutRendererProps) {
    const {data: layout, isLoading: layoutLoading, isError} = useFormLayout(useCaseKey)
    const {definitions, isLoading: defsLoading} = useDefinitions(entityType, typeCode)

    if (defsLoading || layoutLoading) {
        return <Skeleton className="h-16 w-full" />
    }

    if (definitions.length === 0) return null

    // No layout (404) or error: fall through to flat list (default behavior)
    if (!layout || isError) {
        return (
            <div className="space-y-4">
                {definitions.map((def) => (
                    <AttributeField
                        key={def.id}
                        definition={def}
                        values={values}
                        onChange={onChange}
                        existingValues={existingValues}
                        templateContext={templateContext}
                    />
                ))}
            </div>
        )
    }

    // Layout-driven rendering
    return (
        <div className="space-y-4">
            <LayoutActiveIndicator useCaseKey={useCaseKey} />
            {layout.layoutSchema.sections.map((section) => (
                <LayoutAttributeSection
                    key={section.key}
                    section={section}
                    definitions={definitions}
                    values={values}
                    onChange={onChange}
                    existingValues={existingValues}
                    templateContext={templateContext}
                />
            ))}
        </div>
    )
}
